import React, { useState } from 'react';
import { API_URL, apiFetch } from '../../services/api';

interface ForgotPasswordModalProps {
    isOpen: boolean;
    onClose: () => void;
}

type Step = 'request' | 'verify' | 'reset';

export const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ isOpen, onClose }) => {
    const [step, setStep] = useState<Step>('request');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [resendTimer, setResendTimer] = useState(0);

    React.useEffect(() => {
        let interval: NodeJS.Timeout;
        if (resendTimer > 0) {
            interval = setInterval(() => {
                setResendTimer(prev => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [resendTimer]);

    if (!isOpen) return null;

    const handleRequestCode = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setMessage('');
        setLoading(true);

        try {
            const res = await apiFetch(`${API_URL}/api/password-reset/request`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            const data = await res.json();

            if (!res.ok) throw new Error(data.error || 'Doğrulama kodu gönderilemedi');

            setMessage(data.message);
            setStep('verify');
            setResendTimer(60);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyCode = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setMessage('');
        setLoading(true);

        try {
            const res = await apiFetch(`${API_URL}/api/password-reset/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, code })
            });
            const data = await res.json();

            if (!res.ok) throw new Error(data.error || 'Kod doğrulanamadı');

            setMessage(data.message);
            setStep('reset');
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setMessage('');

        if (newPassword !== newPasswordConfirm) {
            setError('Şifreler uyuşmuyor.');
            return;
        }

        if (newPassword.length < 8) {
            setError('Şifre en az 8 karakter uzunluğunda olmalıdır.');
            return;
        }

        if (!/[A-Z]/.test(newPassword)) {
            setError('Şifre en az bir büyük harf (A-Z) içermelidir.');
            return;
        }

        if (!/[a-z]/.test(newPassword)) {
            setError('Şifre en az bir küçük harf (a-z) içermelidir.');
            return;
        }

        if (!/\d/.test(newPassword)) {
            setError('Şifre en az bir rakam (0-9) içermelidir.');
            return;
        }

        if (!/[!@#$%^&*()_+\-=\[\]{}|;':",.<>?/\\~`]/.test(newPassword)) {
            setError('Şifre en az bir sembol/özel karakter (!@#$%^&* vb.) içermelidir.');
            return;
        }

        setLoading(true);

        try {
            const res = await apiFetch(`${API_URL}/api/password-reset/reset`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, code, newPassword })
            });
            const data = await res.json();

            if (!res.ok) throw new Error(data.error || 'Şifre sıfırlanamadı');

            setMessage(data.message);
            setTimeout(() => {
                onCloseModal();
            }, 3000);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const onCloseModal = () => {
        setStep('request');
        setEmail('');
        setCode('');
        setNewPassword('');
        setNewPasswordConfirm('');
        setError('');
        setMessage('');
        setResendTimer(0);
        onClose();
    };

    const isPasswordStrong = (pwd: string) => {
        return pwd.length >= 8 &&
            /[A-Z]/.test(pwd) &&
            /[a-z]/.test(pwd) &&
            /\d/.test(pwd) &&
            /[!@#$%^&*()_+\-=\[\]{}|;':",.<>?/\\~`]/.test(pwd);
    };

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onCloseModal}></div>

            {/* Modal Content */}
            <div className="relative z-10 w-full max-w-md mx-4 bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl shadow-2xl p-6 animate-in fade-in zoom-in duration-300">
                <button
                    onClick={onCloseModal}
                    className="absolute top-4 right-4 text-white/60 hover:text-white"
                >
                    ✕
                </button>

                <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-white mb-2">Şifremi Unuttum</h2>
                    <p className="text-white/70 text-sm">
                        {step === 'request' && 'Kayıtlı e-posta adresinizi girin'}
                        {step === 'verify' && 'E-postanıza gelen doğrulama kodunu girin'}
                        {step === 'reset' && 'Yeni güçlü şifrenizi belirleyin'}
                    </p>
                </div>

                {error && <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-2 rounded-xl mb-4 text-sm">{error}</div>}
                {message && <div className="bg-emerald-500/20 border border-emerald-500/50 text-emerald-200 px-4 py-2 rounded-xl mb-4 text-sm">{message}</div>}

                {step === 'request' && (
                    <form onSubmit={handleRequestCode} className="space-y-4">
                        <div>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                placeholder="E-posta Adresi"
                                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold rounded-xl hover:shadow-lg hover:from-blue-600 hover:to-cyan-600 transition-all"
                        >
                            {loading ? 'Gönderiliyor...' : 'Doğrulama Kodu Gönder'}
                        </button>
                    </form>
                )}

                {step === 'verify' && (
                    <form onSubmit={handleVerifyCode} className="space-y-4">
                        <div>
                            <input
                                type="text"
                                required
                                maxLength={6}
                                value={code}
                                onChange={e => setCode(e.target.value)}
                                placeholder="6 Haneli Kod"
                                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-center tracking-[0.5em] text-xl placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold rounded-xl hover:shadow-lg hover:from-blue-600 hover:to-cyan-600 transition-all"
                        >
                            {loading ? 'Doğrulanıyor...' : 'Kodu Doğrula'}
                        </button>
                        
                        <button
                            type="button"
                            disabled={loading || resendTimer > 0}
                            onClick={handleRequestCode}
                            className={`w-full py-3 border font-bold rounded-xl transition-all ${resendTimer > 0 ? 'bg-white/5 border-white/5 text-white/40 cursor-not-allowed' : 'bg-white/10 border-white/20 text-white hover:bg-white/20 hover:border-white/30'}`}
                        >
                            {resendTimer > 0 ? `Kodu Yeniden Gönder (${resendTimer}s)` : 'Kodu Yeniden Gönder'}
                        </button>

                        <button
                            type="button"
                            onClick={() => setStep('request')}
                            className="w-full text-center text-white/60 hover:text-white text-sm"
                        >
                            Geri Dön
                        </button>
                    </form>
                )}

                {step === 'reset' && (
                    <form onSubmit={handleResetPassword} className="space-y-4">
                        <div>
                            <input
                                type="password"
                                required
                                minLength={8}
                                value={newPassword}
                                onChange={e => setNewPassword(e.target.value)}
                                placeholder="Yeni Şifre (En az 8 karakter)"
                                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Password rules hints */}
                        <div className="p-3 bg-white/5 rounded-xl text-xs space-y-1 text-white/70">
                            <p className="font-semibold text-white/90 mb-1">Şifre Kuralları:</p>
                            <p className={newPassword.length >= 8 ? "text-emerald-400" : "text-white/60"}>
                                {newPassword.length >= 8 ? "✓" : "•"} En az 8 karakter
                            </p>
                            <p className={/[A-Z]/.test(newPassword) ? "text-emerald-400" : "text-white/60"}>
                                {/[A-Z]/.test(newPassword) ? "✓" : "•"} En az bir büyük harf (A-Z)
                            </p>
                            <p className={/[a-z]/.test(newPassword) ? "text-emerald-400" : "text-white/60"}>
                                {/[a-z]/.test(newPassword) ? "✓" : "•"} En az bir küçük harf (a-z)
                            </p>
                            <p className={/\d/.test(newPassword) ? "text-emerald-400" : "text-white/60"}>
                                {/\d/.test(newPassword) ? "✓" : "•"} En az bir rakam (0-9)
                            </p>
                            <p className={/[!@#$%^&*()_+\-=\[\]{}|;':",.<>?/\\~`]/.test(newPassword) ? "text-emerald-400" : "text-white/60"}>
                                {/[!@#$%^&*()_+\-=\[\]{}|;':",.<>?/\\~`]/.test(newPassword) ? "✓" : "•"} En az bir sembol (!@#$% vb.)
                            </p>
                        </div>

                        <div>
                            <input
                                type="password"
                                required
                                minLength={8}
                                value={newPasswordConfirm}
                                onChange={e => setNewPasswordConfirm(e.target.value)}
                                placeholder="Yeni Şifreyi Doğrula"
                                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={loading || !isPasswordStrong(newPassword) || newPassword !== newPasswordConfirm}
                            className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-xl hover:shadow-lg hover:from-emerald-600 hover:to-teal-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Sıfırlanıyor...' : 'Şifreyi Yenile'}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};
